import { Link } from 'react-router-dom';
import { useSiteContent } from '@/context/SiteContentContext';
import { useTheme } from '@/context/ThemeContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

export default function AboutSection() {
  const { content } = useSiteContent();
  const { about } = content;
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  const bg = light ? 'bg-white' : 'bg-[#0D1B2A]';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const mutedHeading = light ? 'text-gray-400' : 'text-white/60';
  const bodyColor = light ? 'text-gray-600' : 'text-white/60';
  const tagBorder = light ? 'border-gray-300 text-gray-500' : 'border-white/20 text-white/70';

  return (
    <section className={`${bg} w-full py-20 transition-colors duration-300`}>
      <div className="w-full px-6 md:px-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Text */}
          <div className="flex-1">
            {settings.aboutBadge && (
              <span
                className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
              >
                {about.badge}
              </span>
            )}
            <h2 className={`text-4xl md:text-5xl font-black ${headingColor} leading-tight mb-6`}>
              {about.headline1}<br />
              <span className={mutedHeading}>{about.headline2}</span>
            </h2>
            <p className={`${bodyColor} text-base leading-relaxed mb-4`}>
              {about.paragraph1}
            </p>
            <p className={`${bodyColor} text-base leading-relaxed mb-8`}>
              {about.paragraph2}
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              {['Lithium LiFePO4', 'AGM Technology', 'Gel Series', 'Marine Certified'].map((tag) => (
                <span key={tag} className={`border ${tagBorder} text-xs px-4 py-2 rounded-full`}>
                  {tag}
                </span>
              ))}
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-white font-bold px-7 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              {about.ctaLabel} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Image */}
          <div className="flex-1 relative">
            <div className="w-full h-[420px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20marine%20battery%20manufacturing%20quality%20control%20facility%20clean%20industrial%20setting%20with%20batteries%20on%20conveyor%20professional%20workers%20in%20safety%20gear%20bright%20clean%20factory%20floor%20high%20tech%20equipment%20american%20manufacturing%20quality%20inspection%20sharp%20commercial%20photography&width=700&height=500&seq=about-factory&orientation=landscape"
                alt="Stingray manufacturing quality"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 text-white px-6 py-4 rounded-lg" style={{ backgroundColor: 'var(--accent)' }}>
              <p className="text-2xl font-black">Global</p>
              <p className="text-xs font-semibold tracking-widest uppercase opacity-80">Distribution</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
